import {
  Trial_Status,
  useDelayQuestionMutation,
  useSkipQuestionMutation,
} from '@generated/graphql';
import ROUTES from 'constants/routes';
import { useDialog } from 'context/dialogContext';
import { useResultModal } from 'context/resultModalContext';
import { useUnit } from 'effector-react';
import { useEffect, useMemo, useState } from 'react';
import {
  $assessmentInfo,
  $assessmentVisibilityStateChangeDetected,
  $questionIndex,
  $questions,
  resetAnswerMetadata,
  resetAssessment,
  setAssessmentStatus,
  setAssessmentVisibilityStateChangeDetected,
} from 'store/assessment/model';

import '@myhrbox/myhrbox-frontend-core/dist/myhrbox-frontend-core.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { resetQuestionTimer, startQuestionTimer } from 'store/assessment/questionTimer';
import { RefetchTrialInfo } from 'store/assessment/types';
import { QuestionPageContext, QuestionPageContextValue } from './context';
import InterviewQuestion from './Interview';
import SingleQuestion from './Single';
import { RemainingTime } from './types';

interface QuestionPageProps {
  refetchTrialInfo: RefetchTrialInfo;
  remainingTime: RemainingTime;
}

const QuestionPage = ({ refetchTrialInfo, remainingTime }: QuestionPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showResultModal } = useResultModal();
  const [assessmentInfo, questions, questionIndex, assessmentVisibilityStateChangeDetected] =
    useUnit([
      $assessmentInfo,
      $questions,
      $questionIndex,
      $assessmentVisibilityStateChangeDetected,
    ]);
  const { showDialog, closeDialog } = useDialog();
  // const [visibilityStateChangeDetected, setVisibilityStateChangeDetected] = useState(false);
  const [isDelayLoading, setIsDelayLoading] = useState(false);
  const [isSkipLoading, setIsSkipLoading] = useState(false);
  const [delayQuestion, { loading: delayQuestionLoading }] = useDelayQuestionMutation();
  const [skipQuestion, { loading: skipQuestionLoading }] = useSkipQuestionMutation();
  const { sessionId, examination } = assessmentInfo || {};
  const isLastQuestion = questionIndex === questions.length - 1;

  const handleFinishTesting = () => {
    resetQuestionTimer();
    setAssessmentStatus(Trial_Status.Completed);
    navigate(ROUTES.ASSESSMENT, { replace: true, state: Trial_Status.Completed });
    showResultModal({
      specialization: examination?.competencyMap.specialization,
      onClose: () => resetAssessment(),
      isSendResult: examination?.isSendResult,
    });
  };

  const handleFinishQuestion = (refetchTrial: boolean = true) => {
    resetQuestionTimer();
    resetAnswerMetadata();
    if (refetchTrial) return refetchTrialInfo();
    return null;
  };

  const contextValue: QuestionPageContextValue = useMemo(
    () => ({
      isDelayLoading,
      setIsDelayLoading,
      isSkipLoading,
      setIsSkipLoading,
      delayQuestion,
      delayQuestionLoading,
      skipQuestion,
      skipQuestionLoading,
      handleFinishTesting,
      handleFinishQuestion,
      isLastQuestion,
      refetchTrialInfo,
      remainingTime,
    }),
    [
      isDelayLoading,
      setIsDelayLoading,
      isSkipLoading,
      setIsSkipLoading,
      delayQuestion,
      delayQuestionLoading,
      skipQuestion,
      skipQuestionLoading,
      handleFinishTesting,
      handleFinishQuestion,
      isLastQuestion,
      refetchTrialInfo,
      remainingTime,
    ],
  );

  const showVisibilityStateChangeDetectedModal = () => {
    showDialog({
      title: t('assessment.proctoring.visibilityStateChangeDetected.title'),
      content: t('assessment.proctoring.visibilityStateChangeDetected.content'),
      confirmText: t('confirm'),
      actionsProps: { sx: { justifyContent: 'center' } },
      onConfirm: async () => {
        closeDialog();
      },
    });
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setAssessmentVisibilityStateChangeDetected(true);
        showVisibilityStateChangeDetectedModal();
      }
    };

    if (assessmentVisibilityStateChangeDetected) {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    } else {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [assessmentVisibilityStateChangeDetected]);

  useEffect(() => {
    startQuestionTimer();
  }, []);

  const isInterview = sessionId && examination?.isInterview;

  return (
    <QuestionPageContext.Provider value={contextValue}>
      {isInterview ? <InterviewQuestion sessionId={sessionId} /> : <SingleQuestion />}
    </QuestionPageContext.Provider>
  );
};

export default QuestionPage;
